import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { ComponentProps, FC } from 'react';

import { Body } from '@growtherapy/sprout-ui';

import { EventTypeEnum } from '../../segment/types';
import { noop } from '../../utils';
import { AppointmentType } from '../schedule-preview/types';
import { selectedClientInfoAtom } from '../state';
import { ClientActivityBetweenSessionCardWrapper } from './ClientActivityBetweenSessionCardWrapper';
import {
  ClientInformationSectionWrapper as ClientInformationSectionConnected,
  ClientInformationSectionWrapper,
} from './ClientInformationSectionWrapper';
import { EPrescribeMedicationsCardWrapper } from './EPrescribeMedicationsCardWrapper';
import { ProgressNoteSectionWrapper } from './ProgressNoteSection';
import { ProviderResourcesCollapsibleContainerWrapper as ProviderResourcesCollapsibleContainer } from './ProviderResourcesCollapsibleContainerWrapper';
import { CLIENT_HAS_LEFT_MESSAGE } from './constants';
import { PreVisitSummaryCardWrapper } from './pre-visit-summary/PreVisitSummaryCardWrapper';
import { hasNewFormsAtomFamily } from './state';
import { useSendClientInfoTabViewTrackingEvent } from './useSendClientInfoTabViewTrackingEvent';
import { getAppointmentType } from './utils';

export interface ClientInformationDrawerBodyProps {
  isEmpty: boolean;
  hasNewForms?: boolean;
  isFollowUpAppointment?: boolean;
  progressNoteSectionComponent: FC<ComponentProps<typeof ProgressNoteSectionWrapper>>;
  clientInformationSectionComponent: FC<ComponentProps<typeof ClientInformationSectionWrapper>>;
  clientActivityBetweenSessionsComponent: FC<ComponentProps<typeof ClientActivityBetweenSessionCardWrapper>>;
  providerResourcesCollapsibleContainerComponent: FC;
  preVisitSummaryCardComponent: FC<ComponentProps<typeof PreVisitSummaryCardWrapper>>;
  ePrescribeMedicationsCardComponent: FC<ComponentProps<typeof EPrescribeMedicationsCardWrapper>>;
}

export function ClientInformationTab({
  isEmpty,
  isFollowUpAppointment,
  progressNoteSectionComponent: ProgressNoteSectionComponent,
  clientInformationSectionComponent: ClientInformationSectionComponentConnected,
  clientActivityBetweenSessionsComponent: ClientActivityBetweenSessionsComponentConnected,
  providerResourcesCollapsibleContainerComponent: ProviderResourcesCollapsibleContainerComponent,
  preVisitSummaryCardComponent: PreVisitSummaryCardComponent,
  ePrescribeMedicationsCardComponent: EPrescribeMedicationsCardComponent,
}: ClientInformationDrawerBodyProps) {
  return (
    <>
      {isEmpty ? (
        <div className='flex flex-col h-full '>
          <div className='pt-6 mt-auto'>
            <Body.Xsmall className='rebrand:text-text-inverse text-center'>{CLIENT_HAS_LEFT_MESSAGE}</Body.Xsmall>
          </div>
        </div>
      ) : (
        <div className='space-y-4'>
          <EPrescribeMedicationsCardComponent />
          <PreVisitSummaryCardComponent />
          <ClientActivityBetweenSessionsComponentConnected />
          <ProviderResourcesCollapsibleContainerComponent />
          <ClientInformationSectionComponentConnected />
          <ProgressNoteSectionComponent className={classNames({ 'order-1 mb-6': isFollowUpAppointment })} />
        </div>
      )}
    </>
  );
}

export function ClientInformationTabWrapper({ isEmpty }: { isEmpty: boolean }) {
  const selectedClient = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClient?.patientShortId;

  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId ?? ''));

  useSendClientInfoTabViewTrackingEvent(patientShortId ?? '', EventTypeEnum.CLIENT_INFO);

  return (
    <ClientInformationTab
      isEmpty={isEmpty}
      hasNewForms={hasNewForms}
      isFollowUpAppointment={getAppointmentType() === AppointmentType.FollowUp}
      progressNoteSectionComponent={ProgressNoteSectionWrapper}
      clientInformationSectionComponent={ClientInformationSectionConnected}
      clientActivityBetweenSessionsComponent={noop}
      providerResourcesCollapsibleContainerComponent={ProviderResourcesCollapsibleContainer}
      preVisitSummaryCardComponent={PreVisitSummaryCardWrapper}
      ePrescribeMedicationsCardComponent={EPrescribeMedicationsCardWrapper}
    />
  );
}
